import {useEffect, useState} from 'react';

import {TabContext} from '@mui/lab';
import {DialogActions, DialogContent, Grid} from '@mui/material';

import {DEFAULT_PROJECTS_SCHEMAS, REGULAR_EXPRESSIONS, REGULAR_EXPRESSIONS_ERROR_TEXTS} from '../../../../const';
import useWorkspaces from '../../../../hooks/providers/useWorkspaces';
import Button from '../../../form/buttons/Button/Button';
import BaseModal from '../../../modal/BaseModal';
import StyledTab from '../../../tabs/StyledTab';
import StyledTabs from '../../../tabs/StyledTabs';
import CreateReportForm from '../CreateReportForm/CreateReportForm';
import ImportedReportNameAlreadyExistsModal from '../ImportedReportNameAlreadyExistsModal/ImportedReportNameAlreadyExistsModal';
import ImportReportForm from '../ImportReportForm/ImportReportForm';

const MODAL_TABS = {
  import: 'import',
  create: 'create'
};

// eslint-disable-next-line complexity
const CreateOrImportReportModal = () => {
  const {isCustomReportCreation, setIsCustomReportCreation} = useWorkspaces();

  const [isCloningExistingReport, setIsCloningExistingReport] = useState(false);
  const [reportToClone, setReportToClone] = useState(null);
  const [reportSchema, setReportSchema] = useState(DEFAULT_PROJECTS_SCHEMAS.gestion);

  const [reportName, setReportName] = useState('');
  const [files, setFiles] = useState(null);
  const [fileUploadError, setFileUploadError] = useState('');
  const [reportContainsSecurityOnData, setReportContainsSecurityOnData] = useState(0);
  const [reportSecurityOnDataRole, setReportSecurityOnDataRole] = useState('');

  const [importedReportNameAvailable, setImportedReportNameAvailable] = useState(false);
  const [importedReportNameNotAvailableModalOpen, setImportedReportNameNotAvailableModalOpen] = useState(false);

  const {createReport, importReport, selectedWorkspaceId, selectedWorkspace, createReportModalOpen: isOpen, setCreateReportModalOpen: setIsOpen} = useWorkspaces();

  const isNameAvailable = !selectedWorkspace?.reports?.map(r => r.report_name).includes(reportName);
  const hasNameError = reportName !== '' && !REGULAR_EXPRESSIONS.labelWithMinimumChars(4).test(reportName);
  const isSecurityOnReportRoleNameValid = REGULAR_EXPRESSIONS.labelWithRangeChars(1, 50).test(reportSecurityOnDataRole);

  const isReportNameValid = reportName && !hasNameError && isNameAvailable;
  const areCustomReportFilesValid = files && files?.length > 0 && fileUploadError === '';

  const closeModal = () => {
    setIsOpen(false);
  };

  const handleImportReportSubmit = async (shouldEraseExistingReport = false) => {
    const importReportData = {
      file: files[0],
      workspaceId: selectedWorkspaceId,
      isEffectiveIdentityRolesRequired: reportContainsSecurityOnData,
      role: reportSecurityOnDataRole
    };
    return importReport(importReportData, shouldEraseExistingReport);
  };

  const onSubmit = async () => {
    if (isCustomReportCreation && !importedReportNameAvailable) {
      setImportedReportNameNotAvailableModalOpen(true);
      return;
    }

    closeModal();

    if (isCustomReportCreation) {
      await handleImportReportSubmit(false);
    } else {
      await createReport({
        name: reportName,
        workspaceId: selectedWorkspaceId,
        clonedReportId: isCloningExistingReport ? reportToClone : null,
        schema: isCloningExistingReport ? null : reportSchema
      });
    }
  };

  const isFormValid = () => {
    if (isCustomReportCreation) {
      return areCustomReportFilesValid && (!reportContainsSecurityOnData || (reportContainsSecurityOnData && isSecurityOnReportRoleNameValid));
    }

    if (isCloningExistingReport) {
      return isReportNameValid && reportToClone !== null;
    }

    return isReportNameValid;
  };

  const getReportNameHelperText = () => {
    if (hasNameError) return REGULAR_EXPRESSIONS_ERROR_TEXTS.labelWithMinimumChars(4);
    if (!isNameAvailable) return 'Ce nom de rapport est déja utilisé dans cet espace';
    return '';
  };

  const handleTabChange = () => {
    setIsCustomReportCreation(!isCustomReportCreation);
  };

  useEffect(() => {
    if (!isOpen) {
      setReportName('');
      setFileUploadError('');
      setFiles(null);
      setReportContainsSecurityOnData(0);
      setReportSecurityOnDataRole('');
      setReportSchema(DEFAULT_PROJECTS_SCHEMAS.gestion);
      setReportToClone(null);
      setIsCloningExistingReport(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (files && files.length > 0) {
      const filenameWithExtension = files[0].name;
      const filename = filenameWithExtension.replace(/\.pbix$/, '');
      setReportName(filename);
      const isImportedReportNameAvailable = !selectedWorkspace?.reports?.map(r => r.report_name).includes(filename);
      setImportedReportNameAvailable(isImportedReportNameAvailable);
    }
  }, [files]);

  const reportNameHelperText = getReportNameHelperText();
  const submitButtonText = isCustomReportCreation ? 'Importer le rapport' : 'Créer le rapport';
  const canSubmit = isFormValid();
  const tabModalSelected = isCustomReportCreation ? MODAL_TABS.import : MODAL_TABS.create;

  return (
    <BaseModal withoutTitle onClose={closeModal} open={isOpen}>
      <TabContext value={tabModalSelected}>
        <StyledTabs onChange={handleTabChange} scrollButtons={false} aria-label="Onglets créer ou importer un rapport">
          <StyledTab label="Importer un rapport" value={MODAL_TABS.import} />
          <StyledTab label="Créer un rapport" value={MODAL_TABS.create} />
        </StyledTabs>
      </TabContext>

      <DialogContent sx={{p: 3}}>
        <Grid container flexDirection="column">
          {!isCustomReportCreation && (
            <CreateReportForm
              reportSchema={reportSchema}
              setReportSchema={setReportSchema}
              reportToClone={reportToClone}
              setReportToClone={setReportToClone}
              isCloningExistingReport={isCloningExistingReport}
              setIsCloningExistingReport={setIsCloningExistingReport}
              reportNameError={hasNameError || !isNameAvailable}
              reportNameHelperText={reportNameHelperText}
              reportName={reportName}
              setReportName={setReportName}
            />
          )}

          {isCustomReportCreation && (
            <ImportReportForm
              fileUploadError={fileUploadError}
              reportContainsSecurityOnData={reportContainsSecurityOnData}
              setReportContainsSecurityOnData={setReportContainsSecurityOnData}
              setFiles={setFiles}
              isSecurityOnReportRoleNameValid={isSecurityOnReportRoleNameValid}
              setReportSecurityOnDataRole={setReportSecurityOnDataRole}
              reportSecurityOnDataRole={reportSecurityOnDataRole}
            />
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{justifyContent: 'center', mb: 2}}>
        <Button sx={{maxWidth: 300}} disabled={!canSubmit} onClick={onSubmit} fullWidth variant="contained" color="secondary" size="large">
          {submitButtonText}
        </Button>
      </DialogActions>
      <ImportedReportNameAlreadyExistsModal
        reportName={reportName}
        isOpen={importedReportNameNotAvailableModalOpen}
        onClose={() => {
          closeModal();
          setImportedReportNameNotAvailableModalOpen(false);
        }}
        onSubmit={async () => {
          await handleImportReportSubmit(true);
          closeModal();
        }}
      />
    </BaseModal>
  );
};

export default CreateOrImportReportModal;
